/* eslint-disable react-refresh/only-export-components -- public host adapter exports components and its context hook */
import { createContext, useContext, type ReactNode } from 'react';

export interface SecurityApi {
  get<T = unknown>(path: string): Promise<T>;
  post<T = unknown>(path: string, body?: unknown): Promise<T>;
  put<T = unknown>(path: string, body?: unknown): Promise<T>;
  patch<T = unknown>(path: string, body?: unknown): Promise<T>;
  download(path: string, filename: string): Promise<void>;
}

export interface FindingReference {
  kind: 'dependabot' | 'code_scanning' | 'secret_scanning';
  repository: string;
  alertNumber: number;
  /** Connector database ID is for evidence lookup; the triplet is the identity. */
  id: number;
}

export interface SecurityHost {
  api: SecurityApi;
  subscriptionSettingsHref?: string;
  user: { id?: string; role?: string; username?: string; email?: string } | null;
  renderRepositoryActions?: (repository: string) => ReactNode;
  renderFindingActions?: (findings: FindingReference[]) => ReactNode;
}

const HostContext = createContext<SecurityHost | null>(null);
export function SecurityHostProvider({ host, children }: { host: SecurityHost; children: ReactNode }) {
  return <HostContext.Provider value={host}>{children}</HostContext.Provider>;
}
export function useSecurityHost(): SecurityHost {
  const host = useContext(HostContext);
  if (!host) throw new Error('Security pages require a SecurityHostProvider');
  return host;
}
export function RepositoryWorkActions({ repository }: { repository: string }) {
  return <>{useSecurityHost().renderRepositoryActions?.(repository)}</>;
}
export function FindingWorkActions({ kind, alerts }: {
  kind: FindingReference['kind'];
  alerts: Array<{ id: number; repository: string; github_alert_number: number }>;
}) {
  return <>{useSecurityHost().renderFindingActions?.(alerts.map(alert => ({
    kind, id: alert.id, repository: alert.repository, alertNumber: alert.github_alert_number,
  })))}</>;
}
